# Route: /tracking/assignments
# Declare / withdraw form assignments — the template ↔ asset-class bindings
# that state which forms a class of asset keeps (F80, ADR-181).
# Permission: FormAssignment:read (list); the declare form and the withdraw
# row action are gated on FormAssignment:create / :delete.
#
# The declare form binds on LOGICAL identity: the template select submits the
# template's logical NAME (TrackingFormOption transform maps name → id) and
# the class select submits the AssetClass id (itself the stable class code).
# Withdrawing stops new entries against the binding; history is unaffected, so
# withdrawn rows stay listed with their Withdrawn badge + timestamp.

page-tracking-assignment-list:
  type: Page
  archetype: list
  meta:
    title: $t.tracking.assignments.meta_title
    route: /tracking/assignments
    layout: shell
    auth: required
  data:
    assignments:
      model: FormAssignment[]
      refresh: on_mount
    templateOptions:
      model: TrackingFormOption[]
      refresh: on_mount
    classOptions:
      model: AssetClassOption[]
      refresh: on_mount
    declareForm:
      model: DeclareAssignmentForm
      initial:
        formName: ""
        assetClassRef: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - assignment-list-header
    - assignment-declare-section
    - assignment-list-table

# ── Header ───────────────────────────────────────────────────────────────────

assignment-list-header:
  type: PageHeader
  content: $t.tracking.assignments.title

# ── Declare form ─────────────────────────────────────────────────────────────
# Creates the binding via the FormAssignment provider create mapping
# (POST /api/tracking/assignments). The dispatcher's xp85-data-changed after the
# create refetches the table so the new row appears in place; API validation
# errors (unknown template / class, duplicate active binding) surface via the
# auto-toast.

assignment-declare-section:
  type: Section
  permission: "FormAssignment:create"
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - assignment-declare-title
    - assignment-declare-form

assignment-declare-title:
  type: Text
  content: $t.tracking.assignments.declare_title
  tokens:
    font_size: $font.size.md
    font_weight: "600"

assignment-declare-form:
  type: Form
  model: DeclareAssignmentForm
  a11y:
    label: $t.tracking.assignments.declare_form_label
  action:
    type: create
    model: FormAssignment
  # Canonical full-width vertical form (mirrors reason-edit / asset-instance
  # create — one form-width rule everywhere). The emitter's global `form`
  # rule lays fields out in a column and stretches each field to full width;
  # a per-field `flex` (row intent) is inert here and left the selects at
  # their narrow intrinsic width, so it is dropped.
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - assignment-field-form
    - assignment-field-class
    - assignment-declare-actions

assignment-field-form:
  type: Input
  input_type: select
  label: $t.tracking.assignments.field_form
  placeholder: $t.tracking.assignments.field_form_placeholder
  bind:
    value: formName
    options: templateOptions
  a11y:
    label: $t.tracking.assignments.field_form_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

assignment-field-class:
  type: Input
  input_type: select
  label: $t.tracking.assignments.field_class
  placeholder: $t.tracking.assignments.field_class_placeholder
  bind:
    value: assetClassRef
    options: classOptions
  a11y:
    label: $t.tracking.assignments.field_class_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

assignment-declare-actions:
  type: FormActions
  submit_label: $t.tracking.assignments.btn_declare

# ── Assignments table ────────────────────────────────────────────────────────

assignment-list-table:
  type: Table
  bind:
    rows: assignments
  empty: assignment-list-empty
  columns:
    - key: formName
      label: $t.tracking.assignments.col_form
      width: 2fr
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: assetClassRef
      label: $t.tracking.assignments.col_class
      width: 2fr
      tokens:
        font_family: $font.family.mono
        font_size: $font.size.sm
    - key: declaredAt
      label: $t.tracking.assignments.col_declared
      format: datetime-short
      width: 1fr
      tokens:
        font_size: $font.size.sm
    - key: status
      label: $t.tracking.assignments.col_status
      width: 1fr
      display: badge
      truncate: true
      variant:
        when: "$row.status == 'Active'"
        value: go
        else: info
    - key: withdrawnAt
      label: $t.tracking.assignments.col_withdrawn
      format: datetime-short
      width: 1fr
      tokens:
        font_size: $font.size.sm
  row_actions:
    - label: $t.tracking.assignments.btn_withdraw
      type: delete
      model: FormAssignment
      confirm: $t.tracking.assignments.withdraw_confirm
      permission: "FormAssignment:delete"
      destructive: true
  a11y:
    label: $t.tracking.assignments.table_label
  tokens:
    border: $border.default
    radius: $radius.md
    background: $color_scheme.light.color.surface-elevated

assignment-list-empty:
  type: EmptyState
  content: $t.tracking.assignments.empty
  body: $t.tracking.assignments.empty_body
  tokens:
    color: $color_scheme.light.color.on-surface-muted
    font_size: $font.size.md
  spatial:
    padding: $spacing.lg
